import { createStore } from 'zustand/vanilla';
import { immer } from 'zustand/middleware/immer';

// Utils
import { StoreInitializer } from '@/utils/store-middlewares/initializeFromRemote';
import { createContextBoundedStore } from '@/providers/helper/createContextBoundedStore';

// Types
import { vodAsset } from '@/types/entities';
import { Avatar } from '@/types/avatar';
import { Product } from '@/types/product';

import { ScriptTemplates as ScriptTemplate } from '@/constants/videoUseCases';

// Slices
import {
  ProductMedia,
  ProductMediaSliceState,
  productMediaSliceInitialState,
  ProductMediaSliceActions,
  createProductMediaSliceActions,
} from './productMediaSlice';
import {
  type AudioBackground,
  type AudioBackgroundSliceState,
  type AudioBackgroundSliceActions,
  initAudioBackgroundSliceState,
  createAudioBackgroundSliceActions,
} from './audioBackgroundSlice';
import { getRandomTopAvatar } from '@/utils/avatar';
import { defaultCaptionsTemplateId } from '@/components/studio/video-configuration/Captions/templates';
import { TemplateName } from '@/features/studio-video-templates/types/template';
import { StepperConfig, StudioVideoVersion } from '@/features/video-generation-stepper/types';
import { IVideoGeneration } from '@/features/studio-videos/types';
import { GeneratedMediaAsset } from '@/types/media';

export enum StudioVideoStatus {
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  FAILED = 'FAILED',
  DRAFT = 'DRAFT',
}

export type TemplateSettings = {
  isAvatarVideoEnabled: boolean;
  scriptTemplate?: ScriptTemplate;
  captionsSettings?: CaptionsSettings;
  avatar?: Avatar | null;
  template?: TemplateName;
};

export type CaptionsSettings = { enabled: boolean; templateId?: string };

export enum OverlayTextPosition {
  top = 'top',
  bottom = 'bottom',
  left = 'left',
  right = 'right',
}

export enum OverlayTextFontFamily {
  COURIER_PRIME_REGULAR = 'COURIER_PRIME_REGULAR',
  FIRA_MONO_MEDIUM = 'FIRA_MONO_MEDIUM',
  KODE_MONO_MEDIUM = 'KODE_MONO_MEDIUM',
  ROBOTO_MONO_MEDIUM = 'ROBOTO_MONO_MEDIUM',
  SONO_MEDIUM = 'SONO_MEDIUM',
  XANH_MONO_REGULAR = 'XANH_MONO_REGULAR',
}

export type OverlayTextSettings = {
  text: string;
  position: OverlayTextPosition;
  fontFamily: string;
  size: number;
  duration: number;
  color: string;
};

export type AnimateImage =
  | string
  | {
      sources: {
        default: string;
        [key: string]: string;
      };
      productType?: string;
      videoPrompt?: string;
      negativePrompt?: string;
      duration?: number;
    };

type StudioVideoDBFields = {
  appKey: string;
  id: string;
  name?: string;
  vodAsset?: vodAsset;
  status: StudioVideoStatus;
  publishedIn?: { appUrl: string }[];
  autoGenerated?: boolean;
  migrated?: boolean;
  createdAt: string;
  updatedAt: string;
  videoPrompt?: string;
  negativePrompt?: string;
  captionsSettings: CaptionsSettings;
  template?: TemplateName | null;
  version?: StudioVideoVersion;
  referenceStudioVideoId?: string;
  stepperConfig?: StepperConfig;
  stepperValues?: Record<string, any>;
  animateImages?: AnimateImage[];
  error?: string;
};

export interface IProjectAssetAction {
  compositionAssetId?: string;
  targetAssetType: 'image' | 'video';
}

export interface IProject {
  assets: {
    [id: string]: {
      id: string;
      type: 'media-asset';
      asset: GeneratedMediaAsset;
      action?: IProjectAssetAction;
    };
  };
  generation: IVideoGeneration;
}

// Entity type
export type StudioVideo = Partial<StudioVideoDBFields> & {
  // Avatar slice
  avatar: Avatar | null;
  isAvatarVideoEnabled: boolean;
  // temporary setting to hide avatar in a middle section (show only media assets)
  hideBubbleAvatar?: boolean;

  // Product slice
  product: Product | null;

  // Video script slice
  scriptTemplate: ScriptTemplate;
  productFeatures: string[];
  targetAudience: string[];
  userPrompt: string;
  videoScript: string;
  isEditedVideoScript: boolean;

  referenceStudioVideoId?: string;

  createdAt?: string;
  overlayTextSettings: OverlayTextSettings;
  status?: StudioVideoStatus;

  project?: IProject;
  videoSettings?: { duration?: string; mode?: string };
} & ProductMedia &
  AudioBackground;

export type StudioVideoPayload = StudioVideo & {
  appKey: string;
  appUrl: string;
  createdBy: { id: string; email: string };
  product: (Product & { appUrl: string }) | null;
  stepperStore?: Record<string, any>;
  stepperConfig?: StepperConfig;
  animateImages?: string[];
  templateKey?: string;
};

// Store type
type StudioVideoStoreState = Partial<StudioVideoDBFields> & {
  // Avatar slice
  avatar: Avatar | null;
  isAvatarVideoEnabled: boolean;

  // Product slice
  product: Product | null;
  brandDescription: string | null;

  // Video script slice
  scriptTemplate: ScriptTemplate;
  productFeatures: string[];
  targetAudience: string[];
  userPrompt: string;
  videoScript: string;
  isEditedVideoScript: boolean;

  referenceStudioVideoId?: string;

  overlayTextSettings: OverlayTextSettings;

  // temporary setting to hide avatar in a middle section (show only media assets)
  hideBubbleAvatar: boolean;
} & ProductMediaSliceState &
  AudioBackgroundSliceState;

type StudioVideoStoreActions = {
  // Avatar slice
  setAvatar: (avatar: Avatar) => void;
  setIsAvatarVideoEnabled: (enabled: boolean) => void;
  setHideBubbleAvatar: (hideBubbleAvatar: boolean) => void;

  // Product slice
  setProduct: (product: Product | null, shallow?: boolean) => void;
  setBrandDescription: (brandDescription: string) => void;

  // Video script slice
  setScriptTemplate: (scriptTemplate: ScriptTemplate) => void;
  setProductFeatures: (productFeatures: string[]) => void;
  setTargetAudience: (targetAudience: string[]) => void;
  setUserPrompt: (userPrompt: string) => void;
  setVideoScript: (videoScript: string, isEdited?: boolean) => void;

  setCaptionsSettings: (captionsSettings: CaptionsSettings) => void;

  setStudioVideo: (studioVideo: StudioVideo) => void;
  getStudioVideo: () => StudioVideo;

  setTemplateSettings: (templateSettings: TemplateSettings) => void;
  getTemplateSettings: () => TemplateSettings;

  setOverlayTextSettings: (overlayTextSettings: OverlayTextSettings) => void;
} & ProductMediaSliceActions &
  AudioBackgroundSliceActions;

type StudioVideoStore = StudioVideoStoreState & StudioVideoStoreActions;

const initialState: StudioVideoStoreState = Object.freeze({
  // Avatar slice
  avatar: getRandomTopAvatar(),
  isAvatarVideoEnabled: true,
  hideBubbleAvatar: false,

  // Product slice
  product: null,
  brandDescription: null,

  ...productMediaSliceInitialState,

  // Video script slice
  scriptTemplate: ScriptTemplate.hypeCheck,
  productFeatures: [],
  targetAudience: [],
  userPrompt: '',
  videoScript: '',
  isEditedVideoScript: false,

  ...initAudioBackgroundSliceState(),

  captionsSettings: {
    enabled: true,
    templateId: defaultCaptionsTemplateId,
  } as const,

  overlayTextSettings: {
    text: '',
    position: OverlayTextPosition.top,
    fontFamily: OverlayTextFontFamily.FIRA_MONO_MEDIUM,
    size: 48,
    duration: 30_000,
    color: '#ffffff',
  },
} satisfies StudioVideoStoreState);

const studioVideoStore: StoreInitializer<StudioVideoStore> = (set, get) => ({
  ...initialState,

  // Avatar slice
  setAvatar: (avatar: Avatar) => {
    set({ avatar });
  },
  setHideBubbleAvatar: (hideBubbleAvatar: boolean) => {
    set({ hideBubbleAvatar });
  },
  setIsAvatarVideoEnabled: (enabled: boolean) => {
    set({ isAvatarVideoEnabled: enabled });
  },

  // Product slice
  setProduct: (product: Product | null, shallow: boolean = false) => {
    if (shallow) {
      set({ product });
      return;
    }

    if (!product?.productId) {
      set((state) => ({
        ...initialState,
        // Preserve avatar slice state
        ...{
          avatar: state.avatar,
          hideBubbleAvatar: state.hideBubbleAvatar,
          isAvatarVideoEnabled: state.isAvatarVideoEnabled,
        },
      }));
      return;
    }

    const templateSettings = get().getTemplateSettings();
    const { productId, description, ...productDetails } = product;
    set(() => ({
      ...initialState,
      // Preserve template settings
      ...templateSettings,
      hideBubbleAvatar: get().hideBubbleAvatar,
      product: {
        ...productDetails,
        productId,
        id: productId,
      },
    }));
  },

  setBrandDescription: (brandDescription: string) => {
    set({ brandDescription });
  },

  ...createProductMediaSliceActions(set),

  // Video script slice
  setScriptTemplate: (scriptTemplate: ScriptTemplate) => {
    set({ scriptTemplate });
  },
  setProductFeatures: (productFeatures: string[]) => {
    set({ productFeatures });
  },
  setTargetAudience: (targetAudience: string[]) => {
    set({ targetAudience });
  },
  setUserPrompt: (userPrompt: string) => {
    set({ userPrompt });
  },
  setVideoScript: (videoScript: string, isEditedVideoScript: boolean = false) => {
    set({ videoScript, isEditedVideoScript });
  },

  setCaptionsSettings: (captionsSettings: CaptionsSettings) => {
    set({ captionsSettings });
  },

  ...createAudioBackgroundSliceActions(set),

  setStudioVideo: (studioVideo: StudioVideo) => {
    if (studioVideo) {
      set({
        ...initialState,
        ...studioVideo,
        ...initAudioBackgroundSliceState(studioVideo),
      });
    }
  },

  getStudioVideo: (): StudioVideo => {
    const {
      product,
      productMedia,
      avatar,
      isAvatarVideoEnabled,
      scriptTemplate,
      productFeatures,
      targetAudience,
      userPrompt,
      videoScript,
      isEditedVideoScript,
      captionsSettings,
      audioBackground,
      referenceStudioVideoId,
      overlayTextSettings,
      status,
      template,
      hideBubbleAvatar,
      playType,
    } = get();

    if (!productMedia) {
      throw new Error(`StudioVideo is incomplete.`);
    }

    return {
      product: product || null,
      productMedia,
      avatar,
      isAvatarVideoEnabled,
      scriptTemplate,
      productFeatures,
      targetAudience,
      userPrompt,
      videoScript,
      isEditedVideoScript,
      captionsSettings,
      audioBackground,
      referenceStudioVideoId,
      overlayTextSettings,
      status,
      template,
      hideBubbleAvatar,
      playType,
    };
  },
  setTemplateSettings: (templateSettings: TemplateSettings) => {
    set((state) => ({
      ...state,
      ...templateSettings,
    }));
  },

  getTemplateSettings: (): TemplateSettings => {
    const { scriptTemplate, isAvatarVideoEnabled, captionsSettings, avatar } = get();
    return { scriptTemplate, isAvatarVideoEnabled, captionsSettings, avatar };
  },

  setOverlayTextSettings: (overlayTextSettings: OverlayTextSettings) => {
    set({ overlayTextSettings });
  },
});

export const { hook: useStudioVideoStore, provider: StudioVideoStoreProvider } = createContextBoundedStore({
  createFunction: () => createStore<StudioVideoStore>()(immer(studioVideoStore)),
});
